import {
  ArrowLeft,
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Headphones,
  Home,
  MapPin,
  Pencil,
  ShoppingBag
} from 'lucide-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PersonalInfo from './PersonalInfo';

const ViewProfile = () => {
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dob: '',
    addressList: [],
    educationDetails: [],
    employmentDetails: [],
    orders: [],
    profilePicture: {}
  });
  const [currentAddressIndex, setCurrentAddressIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Get auth data directly from Redux state
  const authState = useSelector((state) => state?.auth?.data);
  const companyState = useSelector((state) => state.companyReducer.company);
  const isAuthenticated = authState?.isAuthenticated;
  const isFlexOffice = companyState?.domain?.type === 'FLEX_OFFICE';

  const accountStatusMap = {
    ACTIVE: { label: 'Active', className: 'bg-green-100 text-green-800' },
    INACTIVE: { label: 'Inactive', className: 'bg-red-100 text-red-800' },
    EMAIL_VERIFIED: { label: 'Verified', className: 'bg-blue-100 text-blue-800' },
    NEW: { label: 'New', className: 'bg-amber-100 text-amber-800' },
    APPLIED: { label: 'Applied', className: 'bg-indigo-100 text-indigo-800' },
    ADMITTED: { label: 'Admitted', className: 'bg-green-100 text-green-800' }
  };
  const accountStatus = accountStatusMap[user?.status] || accountStatusMap.NEW;

  useEffect(() => {
    const loadUserData = () => {
      try {
        setLoading(true);

        if (!authState) {
          setLoading(false);
          return;
        }

        // Extract user data from auth state - handle different possible structures
        const userData = authState?.user || authState;

        // Format addresses from auth state
        const formattedAddresses = userData.addresses || userData.addressList || [];

        // Map auth state data to user object
        const mappedUser = {
          ...userData, // Spread all user data first
          firstName: userData.firstName || userData.firstname || '',
          lastName: userData.lastName || userData.lastname || '',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || userData.phone || '',
          dob: userData.dob || '',
          addressList: formattedAddresses,
          educationDetails: userData.educationDetails || userData.education || [],
          employmentDetails: userData.employmentDetails || userData.employment || [],
          orders: userData.orders || [],
          profilePicture: userData.profilePicture || {},
          status: userData.status,
          // Preserve other fields like primaryId, accountId, etc.
          primaryId: userData.primaryId,
          accountId: userData.accountId,
          // Get created date for member since
          createdDate: userData.createdDate || userData.created || userData.createdAt || new Date().toISOString()
        };

        setUser(mappedUser);

        localStorage.setItem("isAuthenticated", "true");
      } catch {
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [authState]);

  const handleEditClick = () => {
    navigate('/edit-profile');
  };

  const handleViewOrders = () => {
    navigate('/my-orders');
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleContactSupport = () => {
    navigate('/contact');
  };

  const handleMyBookings = () => {
    navigate('/my-bookings');
  };

  const handleNextAddress = () => {
    if (currentAddressIndex < user.addressList.length - 1) {
      setCurrentAddressIndex(currentAddressIndex + 1);
    }
  };

  const handlePreviousAddress = () => {
    if (currentAddressIndex > 0) {
      setCurrentAddressIndex(currentAddressIndex - 1);
    }
  };

  const [domainType, setDomainType] = useState("");

  useEffect(() => {
    const domain = localStorage.getItem("domainType") || "EDUCATION";
    setDomainType(domain);
  }, []);

  // Enhanced address formatting for better display
  const formatAddress = (address) => {
    if (!address) return 'Not specified';

    const parts = [
      address.streetName1 || address.street1 || address.addressLine1,
      address.streetName2 || address.street2 || address.addressLine2,
      address.city,
      address.state,
      address.zipcode || address.zipCode || address.postalCode,
      address.country
    ].filter(part => part && part.trim() !== '');

    return parts.length > 0 ? parts.join(', ') : 'Not specified';
  };

  // Get address type with proper formatting
  const getAddressType = (address) => {
    if (!address?.type) return 'Primary Address';
    return address.type.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  };

  // Get address type icon
  const getAddressTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'work':
        return <Building2 className="text-blue-600" aria-hidden="true" />;
      case 'home':
        return <Home className="text-green-600" aria-hidden="true" />;
      case 'office':
        return <Building2 className="text-purple-600" aria-hidden="true" />;
      default:
        return <MapPin className="text-gray-600" aria-hidden="true" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="w-16 h-16 border-t-4 border-blue-500 border-solid rounded-full animate-spin"></div>
          <p className="mt-4 text-lg text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section with Back Button */}
          <div className="pt-8 pb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex flex-col">
                <div className="flex items-center space-x-4 mb-2">
                  <button
                    onClick={handleBackClick}
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 group"
                  >
                    <ArrowLeft className="group-hover:-translate-x-1 transition-transform duration-200" aria-hidden="true" />
                    <span className="font-medium">Back</span>
                  </button>
                </div>
                <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
                <p className="text-gray-600 mt-2">Manage your personal information and preferences</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleEditClick}
                  className="bg-gradient-to-r from-slate-800 to-gray-900 hover:from-slate-900 hover:to-black text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 font-medium flex items-center"
                >
                  <Pencil className="mr-2" aria-hidden="true" />
                  Edit Profile
                </button>
                {/* {domainType === "RETAIL" && (
                  <button
                    onClick={handleViewOrders}
                    className="border border-slate-700 text-slate-700 hover:border-slate-800 hover:bg-slate-50 px-6 py-3 rounded-xl shadow hover:shadow-md transition-all duration-200 font-medium flex items-center"
                  >
                    <ShoppingBag className="mr-2" aria-hidden="true" />
                    View Orders
                  </button>
                )} */}
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Personal Info */}
            <div className="xl:col-span-2 space-y-8">
              {/* Pass the entire user object to PersonalInfo */}
              <PersonalInfo
                user={user}
                isAuthenticated={isAuthenticated}
                disabled={true}
              />

              {/* Address Display Section - Always show if addresses exist */}
              {user.addressList?.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                          <MapPin className="w-6 h-6 text-white" aria-hidden="true" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-800">Address Details</h2>
                          <p className="text-sm text-gray-500">Your saved addresses</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          {user.addressList.length} Address{user.addressList.length !== 1 ? 'es' : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Address Navigation Controls */}
                    {user.addressList.length > 1 && (
                      <div className="flex items-center justify-between mb-6 px-2">
                        <button
                          onClick={handlePreviousAddress}
                          disabled={currentAddressIndex === 0}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${currentAddressIndex === 0
                            ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                            : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                        >
                          <ChevronLeft aria-hidden="true" />
                          <span className="font-medium">Previous</span>
                        </button>

                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600 font-medium">
                            Address {currentAddressIndex + 1} of {user.addressList.length}
                          </span>
                          <div className="flex items-center space-x-1 ml-2">
                            {user.addressList.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => setCurrentAddressIndex(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${idx === currentAddressIndex
                                  ? 'bg-blue-600 w-4'
                                  : 'bg-gray-300 hover:bg-gray-400'
                                  }`}
                              />
                            ))}
                          </div>
                        </div>

                        <button
                          onClick={handleNextAddress}
                          disabled={currentAddressIndex === user.addressList.length - 1}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${currentAddressIndex === user.addressList.length - 1
                            ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                            : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                        >
                          <span className="font-medium">Next</span>
                          <ChevronRight aria-hidden="true" />
                        </button>
                      </div>
                    )}

                    {/* Current Address Display */}
                    <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-6 border border-blue-100">
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white border border-blue-200 shadow-sm">
                            {getAddressTypeIcon(user.addressList[currentAddressIndex]?.type)}
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {getAddressType(user.addressList[currentAddressIndex])}
                            </h3>
                            {user.addressList[currentAddressIndex]?.isPrimary && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                Primary Address
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Main Address Display */}
                      <div className="mb-6">
                        <div className="flex items-start space-x-3">
                          <MapPin className="text-blue-500 mt-1 flex-shrink-0" aria-hidden="true" />
                          <div>
                            <p className="text-gray-800 text-lg leading-relaxed">
                              {formatAddress(user.addressList[currentAddressIndex])}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Address Details Grid - Enhanced with better spacing */}
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">Address Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                Country
                              </label>
                              <p className="text-gray-900 font-medium">
                                {user.addressList[currentAddressIndex]?.country || 'Not specified'}
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                City
                              </label>
                              <p className="text-gray-900 font-medium">
                                {user.addressList[currentAddressIndex]?.city || 'Not specified'}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                State
                              </label>
                              <p className="text-gray-900 font-medium">
                                {user.addressList[currentAddressIndex]?.state || 'Not specified'}
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                {user.addressList[currentAddressIndex]?.country === 'USA' ? 'Zip Code' : 'Pincode'}
                              </label>
                              <p className="text-gray-900 font-medium">
                                {user.addressList[currentAddressIndex]?.zipcode ||
                                  user.addressList[currentAddressIndex]?.zipCode ||
                                  user.addressList[currentAddressIndex]?.postalCode ||
                                  'Not specified'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* All Addresses Summary - Only show if more than 1 address */}
                    {user.addressList.length > 1 && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-base font-semibold text-gray-800">All Addresses</h4>
                          <span className="text-sm text-gray-500">
                            Click to view details
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {user.addressList.map((address, index) => (
                            <div
                              key={index}
                              className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer ${index === currentAddressIndex
                                ? 'border-blue-300 bg-blue-50 shadow-sm transform scale-[1.02]'
                                : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/50'
                                }`}
                              onClick={() => setCurrentAddressIndex(index)}
                            >
                              <div className="flex items-start space-x-3">
                                <div className="mt-1">
                                  {getAddressTypeIcon(address?.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <span className={`text-sm font-medium ${index === currentAddressIndex ? 'text-blue-700' : 'text-gray-900'
                                        }`}>
                                        {getAddressType(address)}
                                      </span>
                                      {address?.isPrimary && (
                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                          Primary
                                        </span>
                                      )}
                                    </div>
                                    {index === currentAddressIndex && (
                                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                                        Current
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-sm truncate ${index === currentAddressIndex ? 'text-blue-600' : 'text-gray-600'}`}>
                                    {formatAddress(address)}
                                  </p>
                                  <div className="flex items-center mt-2 text-xs text-gray-500">
                                    <span className="mr-3">
                                      {address?.city || 'City'}
                                    </span>
                                    <span>
                                      {address?.country || 'Country'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Education Section */}
              {!isFlexOffice && domainType !== "RETAIL" && user.educationDetails?.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <GraduationCap className="w-6 h-6 text-white" aria-hidden="true" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-800">Education Details</h2>
                        <p className="text-sm text-gray-500">Your academic background</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    {user.educationDetails.map((edu, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                              </svg>
                              <span className="font-medium text-gray-700">Education {index + 1}</span>
                            </div>
                            {edu.currentlyStudying && (
                              <span className="bg-green-100 text-green-800 px-2.5 py-0.5 rounded-full text-xs font-medium">
                                Currently Studying
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Education Level */}
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                Education Level
                              </label>
                              <p className="text-gray-900 font-medium">
                                {edu.educationLevel
                                  ? edu.educationLevel.replace(/_/g, ' ').toLowerCase().replace(/^\w|\s\w/g, (c) => c.toUpperCase())
                                  : 'Not specified'}
                              </p>
                            </div>

                            {/* Degree */}
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                Degree
                              </label>
                              <p className="text-gray-900 font-medium">
                                {edu.degree || 'Not specified'}
                              </p>
                            </div>

                            {/* School Name */}
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                School Name
                              </label>
                              <p className="text-gray-900 font-medium">
                                {edu.schoolName || 'Not specified'}
                              </p>
                            </div>

                            {/* Field of Study */}
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                Field of Study
                              </label>
                              <p className="text-gray-900 font-medium">
                                {edu.fieldOfStudy || 'Not specified'}
                              </p>
                            </div>

                            {/* Duration */}
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                                Duration
                              </label>
                              <p className="text-gray-900 font-medium">
                                {edu.startDate && (
                                  <>
                                    {moment(edu.startDate).format('MMM YYYY')}
                                    {' - '}
                                    {edu.currentlyStudying
                                      ? 'Present'
                                      : edu.endDate
                                        ? moment(edu.endDate).format('MMM YYYY')
                                        : 'Not specified'}
                                  </>
                                )}
                                {!edu.startDate && 'Not specified'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Employment Section */}
              {user.employmentDetails?.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-violet-50">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-white" aria-hidden="true" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-800">Employment</h2>
                        <p className="text-sm text-gray-500">Your work experience</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    {user.employmentDetails.map((emp, index) => (
                      <div key={index} className={`p-4 rounded-lg border ${index > 0 ? 'mt-4' : ''} border-gray-200`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-gray-900">{emp.position || 'Not specified'}</h3>
                            <p className="text-sm text-gray-600">{emp.company || 'Not specified'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">{emp.duration || 'Not specified'}</p>
                            {emp.location && (
                              <p className="text-sm text-gray-600">{emp.location}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Quick Actions & Summary */}
            <div className="space-y-6">
              {/* Profile Summary Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 text-lg mb-4">Profile Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Status</span>
                    <span className={`${accountStatus.className} px-2 py-1 rounded-full text-xs font-medium`}>
                      {accountStatus.label}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Member Since</span>
                    <span className="text-gray-900 font-medium">
                      {user.createdDate ? moment(user.createdDate).format('MMM YYYY') :
                        user.dob ? moment(user.dob).format('MMM YYYY') : 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Addresses</span>
                    <span className="text-gray-900 font-medium">
                      {user.addressList?.length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Email Verified</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.email ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {user.email ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Phone Verified</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.phoneNumber ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {user.phoneNumber ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 text-lg mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  {/* <button
                    onClick={handleEditClick}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 flex items-center space-x-3 group"
                  >
                    <Edit className="text-blue-600 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-medium">Update Profile</span>
                  </button> */}

                  {domainType === "RETAIL" && (
                    <>
                      <button
                        onClick={handleViewOrders}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all duration-200 flex items-center space-x-3 group"
                      >
                        <ShoppingBag className="text-green-600 group-hover:scale-110 transition-transform duration-200" />
                        <span className="font-medium">View Orders</span>
                      </button>

                    </>
                  )}

                  {isFlexOffice && user.addressList?.length > 0 && (
                    <button
                      onClick={handleMyBookings}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 flex items-center space-x-3 group"
                    >
                      <CalendarDays className="text-purple-600 group-hover:scale-110 transition-transform duration-200" aria-hidden="true" />
                      <span className="font-medium">My Bookings</span>
                    </button>
                  )}

                  <button
                    onClick={handleContactSupport}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-all duration-200 flex items-center space-x-3 group"
                  >
                    <Headphones className="text-red-600 group-hover:scale-110 transition-transform duration-200" aria-hidden="true" />
                    <span className="font-medium">Contact Support</span>
                  </button>
                </div>
              </div>

              {/* Additional Info Card */}
              {/* <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                  <h3 className="font-bold text-gray-900 text-lg mb-4">Additional Information</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Domain Type</h4>
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${domainType === 'RETAIL' ? 'bg-green-500' : domainType === 'EDUCATION' ? 'bg-blue-500' : 'bg-purple-500'}`}></div>
                        <span className="text-gray-900 font-medium">{domainType}</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Account ID</h4>
                      <p className="text-gray-900 font-mono text-sm">{user.accountId || 'Not available'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Primary ID</h4>
                      <p className="text-gray-900 font-mono text-sm">{user.primaryId || 'Not available'}</p>
                    </div>
                  </div>
                </div> */}
            </div>
          </div>

          {/* Footer Note */}
          <div className="pt-12 flex justify-center">
            <div className="text-center text-gray-500 text-sm max-w-lg">
              <p>Your profile information is securely stored and used to enhance your experience across our platform.</p>
              <p className="mt-2">Last updated: {moment().format('MMM D, YYYY')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProfile;
